/**
 * Filet de stockage (Sentry JAVASCRIPT-REACT-58).
 *
 * Safari lève `SecurityError` dès qu'on touche `window.localStorage` ou
 * `window.sessionStorage` quand le stockage du site est interdit (réglage
 * « Bloquer tous les cookies », certains navigateurs intégrés). 29 fichiers de
 * l'app lisent le stockage, dont des fournisseurs posés à la racine : un seul
 * accès non protégé remplace toute l'app par l'écran d'erreur.
 *
 * Appelé par `polyfills.ts`, premier import de `main.tsx`, donc avant tout
 * autre module. La sonde ne fait qu'une LECTURE : un quota plein à l'écriture
 * ne déclenche pas le filet et ne cache pas les données existantes.
 * Coût accepté : sans stockage, langue et panier ne survivent pas au rechargement.
 */
export function createMemoryStorage(): Storage {
  const data = new Map<string, string>();
  return {
    get length() {
      return data.size;
    },
    key: (index: number) => Array.from(data.keys())[index] ?? null,
    getItem: (key: string) => data.get(key) ?? null,
    setItem: (key: string, value: string) => {
      data.set(key, String(value));
    },
    removeItem: (key: string) => {
      data.delete(key);
    },
    clear: () => {
      data.clear();
    },
  };
}

const STORAGE_NAMES = ["localStorage", "sessionStorage"] as const;
type StorageName = (typeof STORAGE_NAMES)[number];

// Rempli par installStorageFallback quand un stockage a été remplacé par le
// filet en mémoire. D'autres modules (lazyWithRetry.ts) en dépendent pour
// savoir qu'une valeur posée dans ce stockage ne survivra pas à un rechargement.
const ephemeralStorages = new Set<StorageName>();

export function isStorageEphemeral(name: StorageName): boolean {
  return ephemeralStorages.has(name);
}

export function installStorageFallback(win: Window & typeof globalThis): void {
  for (const name of STORAGE_NAMES) {
    try {
      win[name].getItem("__flowcut_storage_probe__");
    } catch {
      try {
        Object.defineProperty(win, name, {
          configurable: true,
          enumerable: true,
          value: createMemoryStorage(),
        });
        ephemeralStorages.add(name);
      } catch {
        // Un navigateur peut exposer localStorage/sessionStorage comme non
        // reconfigurable : defineProperty lève alors à son tour. Sentry n'est
        // pas encore initialisé à ce stade (polyfills.ts tourne avant le reste
        // de main.tsx) : ne pas planter ici, laisser le stockage tel quel
        // plutôt que de remplacer toute la page par un écran blanc.
      }
    }
  }
}
